import { LocalDataError } from '../types.js';
import { newId } from '../domain/ids.js';
import { nowIso } from '../domain/time.js';
import { canonicalJson, sha256Hex } from './canonical.js';
export class SyncQueueRepository {
    driver;
    context;
    constructor(driver, context) {
        this.driver = driver;
        this.context = context;
    }
    /**
     * Build the INSERT for an operation so callers can commit it atomically
     * with the business rows it describes. The payload is stamped with the
     * device identity - the server derives nothing from it, but it makes the
     * queue self-describing for support export.
     */
    async prepare(kind, aggregateId, payload) {
        const opId = newId();
        const envelope = {
            opId,
            kind,
            aggregateId,
            deviceId: this.context.deviceId,
            tenantId: this.context.tenantId,
            propertyId: this.context.propertyId,
            payload,
        };
        const payloadJson = canonicalJson(envelope);
        const contentHash = await sha256Hex(payloadJson);
        return {
            opId,
            contentHash,
            statement: {
                sql: `INSERT INTO sync_queue (op_id, seq, kind, aggregate_id, payload_json, content_hash, created_at)
              VALUES (?, (SELECT COALESCE(MAX(seq), 0) + 1 FROM sync_queue), ?, ?, ?, ?, ?)`,
                params: [opId, kind, aggregateId, payloadJson, contentHash, nowIso()],
            },
        };
    }
    /** Enqueue standalone (own transaction). */
    async enqueue(kind, aggregateId, payload) {
        const prepared = await this.prepare(kind, aggregateId, payload);
        await this.driver.batch([prepared.statement]);
        const op = await this.get(prepared.opId);
        if (!op)
            throw new LocalDataError('QUEUE_WRITE_LOST', 'Enqueued operation not found after insert');
        return op;
    }
    async get(opId) {
        const rows = await this.driver.query({
            sql: 'SELECT * FROM sync_queue WHERE op_id = ?',
            params: [opId],
        });
        return rows[0] ? mapRow(rows[0]) : null;
    }
    /** Operations awaiting delivery, in drain order. */
    async pending(limit = 50) {
        const rows = await this.driver.query({
            sql: `SELECT * FROM sync_queue WHERE state IN ('PENDING','SENT') ORDER BY seq LIMIT ?`,
            params: [limit],
        });
        return rows.map(mapRow);
    }
    /** Record a delivery attempt starting (engine bookkeeping, Phase 4). */
    async markSent(opId) {
        await this.driver.execute({
            sql: `UPDATE sync_queue
            SET state = 'SENT', attempts = attempts + 1, last_attempt_at = ?
            WHERE op_id = ? AND state IN ('PENDING','SENT')`,
            params: [nowIso(), opId],
        });
    }
    async markDone(opId) {
        await this.driver.execute({
            sql: `UPDATE sync_queue SET state = 'DONE', last_error = NULL WHERE op_id = ?`,
            params: [opId],
        });
    }
    async markFailed(opId, error) {
        await this.driver.execute({
            sql: `UPDATE sync_queue SET state = 'PENDING', last_error = ? WHERE op_id = ?`,
            params: [error.slice(0, 500), opId],
        });
    }
    async markRejected(opId, error) {
        await this.driver.execute({
            sql: `UPDATE sync_queue SET state = 'REJECTED', last_error = ? WHERE op_id = ?`,
            params: [error.slice(0, 500), opId],
        });
    }
    async counts() {
        const rows = await this.driver.query({
            sql: 'SELECT state, COUNT(*) AS n FROM sync_queue GROUP BY state',
        });
        const out = { PENDING: 0, SENT: 0, DONE: 0, REJECTED: 0 };
        for (const row of rows)
            out[String(row.state)] = Number(row.n);
        return out;
    }
    /** Prune acknowledged operations older than the retention window. */
    async pruneDone(olderThanIso) {
        const result = await this.driver.execute({
            sql: `DELETE FROM sync_queue WHERE state = 'DONE' AND created_at < ?`,
            params: [olderThanIso],
        });
        return result.changes;
    }
}
function mapRow(row) {
    return {
        opId: String(row.op_id),
        seq: Number(row.seq),
        kind: String(row.kind),
        aggregateId: String(row.aggregate_id),
        payloadJson: String(row.payload_json),
        contentHash: String(row.content_hash),
        createdAt: String(row.created_at),
        attempts: Number(row.attempts),
        lastAttemptAt: row.last_attempt_at == null ? null : String(row.last_attempt_at),
        lastError: row.last_error == null ? null : String(row.last_error),
        state: String(row.state),
    };
}
//# sourceMappingURL=queue.js.map